import { ComponentRef, Injectable, Type } from '@angular/core';
import { ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { AfterCreateFun, BaseDialog, OverlayRequest } from './overlay.model';
import {
  ConfirmConfig,
  ConfirmDialogComponent,
} from '@shared/components/dialog/confirm-dialog/confirm-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class OverlayService {
  public readonly overlayRequest$: ReplaySubject<OverlayRequest<BaseDialog>> =
    new ReplaySubject(1);
  public readonly markForCheck$: Subject<void> = new Subject();

  public render<T extends BaseDialog>(
    component: Type<T>,
    afterCreate?: AfterCreateFun<T>,
    afterDestroy?: () => unknown | void,
  ) {
    this.overlayRequest$.next({
      component: component,
      afterCreate: ((ref: ComponentRef<T>) => {
        ref.instance.closeRequested
          .pipe(takeUntil(ref.instance.componentDestroyed))
          .subscribe(() => {
            ref.destroy();
            this.markForCheck$.next();
          });

        if (afterCreate) {
          afterCreate(ref);
        }
      }) as AfterCreateFun<BaseDialog>,
      afterDestroy,
    });
  }

  /**
   * Renders a confirm dialog, when submitted the provided callback function is called
   * @param config
   * @param callback
   */
  public confirmDialog(config: ConfirmConfig, callback: () => void) {
    this.overlayRequest$.next({
      component: ConfirmDialogComponent,
      afterCreate: ((ref: ComponentRef<ConfirmDialogComponent>) => {
        ref.instance.config = config;
        ref.instance.submittedSuccessfully.pipe(take(1)).subscribe(() => {
          callback();
          ref.instance.closeRequested.emit();
        });
        ref.instance.closeRequested
          .pipe(takeUntil(ref.instance.componentDestroyed))
          .subscribe(() => {
            ref.destroy();
            this.markForCheck$.next();
          });
      }) as AfterCreateFun<BaseDialog>,
    });
  }
}
